import {
  Divider,
  Illustration,
  LinkButton,
  Text,
  TextArea,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useHandleChange } from "../../utils";

type SuccessPageStateType = {
  showAdditionalInfoTextArea: boolean;
  additionalInformationText: string;
};

type SuccessPageProps = {
  openTextArea?: boolean | undefined;
  additionalInformation?: string | undefined;
};

export function SuccessPage(props: SuccessPageProps) {
  const { openTextArea, additionalInformation } = props;
  const { t } = useTranslation();
  const handleChange = useHandleChange();
  const [successPageValues, setSuccessPageValues] =
    useState<SuccessPageStateType>({
      showAdditionalInfoTextArea: false,
      additionalInformationText: "",
    });

  useEffect(() => {
    if (openTextArea) {
      setSuccessPageValues((currValues) => ({
        ...currValues,
        showAdditionalInfoTextArea: true,
      }));
    }
  }, [openTextArea]);

  return (
    <div className={styles.heading}>
      <Text
        as="h1"
        style="form/page-title"
        testID="identity-verification-section-title"
      >
        {t("Income Verification")}
      </Text>
      <Text
        style="form/body-primary"
        color="text-weak"
        testID="identity-verification-section-subtitle"
      >
        {t(
          "Your verification details have been submitted. Please continue to the next step."
        )}
      </Text>
      <div className={styles.illustration}>
        <Illustration
          aria-hidden="true"
          name="verification-success"
          size={132}
        />
      </div>
      <Trans>
        <Text style="form/body-secondary" color="text-weak">
          <LinkButton
            textStyle="form/body-secondary"
            linkStyle="monochrome"
            textColor="text-weak"
            testID="income-verification-success-page-add-info-button"
            onPress={() => {
              setSuccessPageValues((currValues) => ({
                ...currValues,
                showAdditionalInfoTextArea: true,
              }));
            }}
          >
            Click here
          </LinkButton>{" "}
          if you wish to add any additional income information. This will be
          included in the report.
        </Text>
      </Trans>
      {successPageValues.showAdditionalInfoTextArea && (
        <>
          <Divider marginVertical="space-6" />
          <Text
            className={styles.paddingBottom}
            color="text-weak"
            style="form/body-secondary"
          >
            {t(
              "Please provide any additional financial information you'd like to include in the report. This may cover other income, account balances with different institutions, or financial details not shown in the connected account that help demonstrate your ability to afford rent. You don’t need to include the specific sources such as pensions or other government benefits, just the amounts and frequency."
            )}
          </Text>
          <TextArea
            label={t("Enter further information here")}
            onChange={(value) => {
              handleChange("income_verification_additional_information", value);
              setSuccessPageValues((currValues) => ({
                ...currValues,
                additionalInformationText: value,
              }));
            }}
            testID="income-verification-additional-info-text-area"
            name="incomeVerificationAdditionalInformationText"
            maxLength={250}
            value={
              successPageValues.additionalInformationText ||
              additionalInformation
            }
          />
        </>
      )}
    </div>
  );
}

const styles = {
  illustration: css({
    display: "flex",
    justifyContent: "center",
    paddingTop: "var(--space-12)",
    paddingBottom: "var(--space-12)",
  }),
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),

  paddingBottom: css({
    paddingBottom: "var(--space-3)",
  }),
};
